// AdminAtlasBlocksPage — Phase 7 Bloc Composer.
// URL: /admin/atlas-blocks/:entityType/:slug
// Displays all blocks for a given entity (appellation/cepage/producer/region)
// grouped into 20 pedagogical categories. Each block can be edited, toggled
// public/enriched, reordered, or deleted. A "Generate all" CTA triggers the
// upstream full atlas generation.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Loader2, AlertCircle, Save, Trash2, Eye, EyeOff,
  ArrowUp, ArrowDown, ExternalLink, Plus, RefreshCw, Languages,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { ATLAS_CATEGORIES, CATEGORY_BY_KEY } from "@/lib/atlasCategories";

const ENTITY_LABEL = {
  appellations: "Appellation",
  cepages: "Cépage",
  producers: "Producteur",
  regions: "Région",
};

const AdminAtlasBlocksPage = () => {
  const { entityType = "appellations", slug } = useParams();
  // Upstream expects singular entity type ("appellation", not "appellations").
  const singular = entityType.replace(/s$/, "");
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(ATLAS_CATEGORIES[0].key);
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null); // { id, status, done_categories, total_categories, created_total, categories_pending }
  const [extraInstructions, setExtraInstructions] = useState("");
  // SEO prewarm — pre-translate this fiche to EN/ES/PT so crawlers see full content.
  const [prewarm, setPrewarm] = useState(null); // {running, done_pairs, total_pairs, current, errors}
  const [prewarmError, setPrewarmError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.listAtlasBlocks(singular, slug);
      setBlocks(Array.isArray(data) ? data : data?.items || data?.blocks || []);
    } catch (e) {
      // Upstream not yet implemented → surface empty state with CTA.
      if (e?.status === 404) setBlocks([]);
      else setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [singular, slug]);

  useEffect(() => { load(); }, [load]);

  const byCategory = useMemo(() => {
    const m = {};
    for (const c of ATLAS_CATEGORIES) m[c.key] = [];
    for (const b of blocks) {
      const k = b.category || "histoire";
      if (!m[k]) m[k] = [];
      m[k].push(b);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    return m;
  }, [blocks]);

  const stats = useMemo(() => {
    const total = blocks.length;
    const publicCount = blocks.filter((b) => b.public).length;
    const enrichedCount = total - publicCount;
    return { total, publicCount, enrichedCount };
  }, [blocks]);

  const handleTogglePublic = async (block) => {
    const next = !block.public;
    setBlocks((bs) => bs.map((b) => (b.id === block.id ? { ...b, public: next } : b)));
    try { await adminApi.updateAtlasBlock(block.id, { public: next }); }
    catch (e) {
      setBlocks((bs) => bs.map((b) => (b.id === block.id ? { ...b, public: !next } : b)));
      alert(`Erreur : ${e?.message}`);
    }
  };

  const handleSaveContent = async (block, content) => {
    setBlocks((bs) => bs.map((b) => (b.id === block.id ? { ...b, content, __saved: false, __saving: true } : b)));
    try {
      await adminApi.updateAtlasBlock(block.id, { content });
      setBlocks((bs) => bs.map((b) => (b.id === block.id ? { ...b, __saved: true, __saving: false } : b)));
      setTimeout(() => setBlocks((bs) => bs.map((b) => (b.id === block.id ? { ...b, __saved: false } : b))), 1500);
    } catch (e) {
      setBlocks((bs) => bs.map((b) => (b.id === block.id ? { ...b, __saving: false } : b)));
      alert(`Erreur : ${e?.message}`);
    }
  };

  const handleDelete = async (block) => {
    if (!window.confirm(`Supprimer ce bloc "${block.title || "sans titre"}" ?`)) return;
    try {
      await adminApi.deleteAtlasBlock(block.id);
      setBlocks((bs) => bs.filter((b) => b.id !== block.id));
    } catch (e) { alert(`Erreur : ${e?.message}`); }
  };

  const handleMove = async (block, dir) => {
    const cat = block.category;
    const list = byCategory[cat] || [];
    const idx = list.findIndex((b) => b.id === block.id);
    const next = idx + dir;
    if (next < 0 || next >= list.length) return;
    const reordered = [...list];
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    const ids = reordered.map((b) => b.id);
    setBlocks((bs) => bs.map((b) => {
      const pos = ids.indexOf(b.id);
      return pos >= 0 && b.category === cat ? { ...b, order: pos } : b;
    }));
    try { await adminApi.reorderAtlasBlocks(cat, ids); }
    catch (e) { alert(`Erreur reorder : ${e?.message}`); load(); }
  };

  // Poll a generation job until it completes, refreshing the block list on
  // each tick so the admin can see progress in real time.
  const pollJob = useCallback(async (jobId) => {
    for (let i = 0; i < 240; i++) { // ~20 min max (5s × 240)
      try {
        const s = await adminApi.getAtlasBlocksJob(jobId);
        setJob(s);
        // Reload blocks every 3 ticks (~15s) to reflect newly created blocks.
        if (i % 3 === 0) await load();
        if (s?.status === "done" || s?.status === "completed" || s?.status === "failed") {
          setJob(s);
          await load();
          setGenerating(false);
          return;
        }
      } catch (e) {
        // If polling itself fails, stop but keep the last known job state visible.
        setError(e?.message || String(e));
        setGenerating(false);
        return;
      }
      await new Promise((r) => setTimeout(r, 5000));
    }
    setGenerating(false);
  }, [load]);

  const handleGenerateAll = async (force = false) => {
    setGenerating(true);
    setError(null);
    setJob(null);
    try {
      const res = await adminApi.generateAtlasBlocks({
        entity_type: singular,
        slug,
        categories: [],
        force,
        extra_instructions: extraInstructions.trim(),
      });
      // Upstream returns {job_id, status:"generating", ...} for async pattern.
      const jobId = res?.job_id || res?.id;
      if (jobId) {
        setJob({ id: jobId, status: res?.status || "generating", ...res });
        pollJob(jobId); // fire-and-forget
      } else {
        await load();
        setGenerating(false);
      }
    } catch (e) {
      setError(e?.message || String(e));
      setGenerating(false);
    }
  };

  const handleGenerateCategory = async (categoryKey) => {
    setGenerating(true);
    setError(null);
    setJob(null);
    try {
      const res = await adminApi.generateAtlasBlocks({
        entity_type: singular,
        slug,
        categories: [categoryKey],
        force: false,
        extra_instructions: extraInstructions.trim(),
      });
      const jobId = res?.job_id || res?.id;
      if (jobId) {
        setJob({ id: jobId, status: res?.status || "generating", ...res });
        pollJob(jobId);
      } else {
        await load();
        setGenerating(false);
      }
    } catch (e) {
      setError(e?.message || String(e));
      setGenerating(false);
    }
  };

  const activeCat = CATEGORY_BY_KEY[activeCategory];
  const activeBlocks = byCategory[activeCategory] || [];

  // Kick a SEO prewarm for this fiche (all 3 non-FR langs) + poll status.
  const handlePrewarmTranslations = async () => {
    setPrewarmError(null);
    try {
      const res = await adminApi.prewarmAtlasTranslations({
        entity_type: singular,
        slug,
        langs: ["en", "es", "pt"],
      });
      if (!res?.ok && res?.detail) { setPrewarmError(res.detail); return; }
      setPrewarm({ running: true, done_pairs: 0, total_pairs: res?.queued_pairs || 3 });
      // Poll every 4 s until finished.
      const tick = async () => {
        try {
          const s = await adminApi.prewarmAtlasStatus();
          setPrewarm(s);
          if (s?.running) setTimeout(tick, 4000);
        } catch (e) { setPrewarmError(e?.message || String(e)); }
      };
      setTimeout(tick, 2000);
    } catch (e) { setPrewarmError(e?.message || String(e)); }
  };

  return (
    <div data-testid="admin-atlas-blocks">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Link to="/admin/app-atlas" className="text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-ink)]" data-testid="atlas-blocks-back">
            <ArrowLeft size={16} />
          </Link>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/50">
              {ENTITY_LABEL[entityType] || entityType} · Bloc composer
            </div>
            <h1 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)]">{slug}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`https://app.geovinum.io/explore/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[13px] hover:bg-[color:var(--gv-mist)]"
            data-testid="atlas-blocks-view-public"
          >
            <ExternalLink size={13} /> Voir en ligne
          </a>
          <button
            onClick={load}
            disabled={loading}
            className="p-2 rounded-[4px] border border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)] disabled:opacity-50"
            title="Rafraîchir"
            data-testid="atlas-blocks-refresh"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Stats + generate all */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4 mb-4 flex items-center justify-between gap-4" data-testid="atlas-blocks-header">
        <div className="flex items-center gap-5 text-[13px]">
          <span><b>{stats.total}</b> blocs au total</span>
          <span className="text-emerald-700">👁 <b>{stats.publicCount}</b> publics</span>
          <span className="text-amber-700">🔒 <b>{stats.enrichedCount}</b> enrichis (app)</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={extraInstructions}
            onChange={(e) => setExtraInstructions(e.target.value)}
            placeholder="Instructions IA (optionnel)…"
            className="gv-input text-[12.5px] w-72"
            data-testid="atlas-blocks-extra-input"
          />
          <button
            onClick={() => handleGenerateAll(false)}
            disabled={generating}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] bg-[color:var(--gv-pink)] text-white text-[13px] disabled:opacity-50"
            data-testid="atlas-blocks-generate-all"
          >
            {generating ? <><Loader2 size={13} className="animate-spin" /> Génération 3-5 min…</> : <><Sparkles size={13} /> Générer tout ({20 - Object.values(byCategory).filter((v) => v.length > 0).length} catégories manquantes)</>}
          </button>
          <button
            onClick={handlePrewarmTranslations}
            disabled={prewarm?.running || stats.publicCount === 0}
            title="Pré-traduire les blocs publics vers EN/ES/PT pour l'indexation SEO"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[color:var(--gv-ink)] text-[13px] hover:bg-[color:var(--gv-mist)] disabled:opacity-50"
            data-testid="atlas-blocks-prewarm-translations"
          >
            {prewarm?.running ? <><Loader2 size={13} className="animate-spin" /> Traduction…</> : <><Languages size={13} /> Pré-traduire (EN·ES·PT)</>}
          </button>
        </div>
      </div>

      {(prewarm || prewarmError) && (
        <div
          className={`border rounded-[5px] p-3 mb-4 text-[13px] flex items-center gap-2 ${
            prewarmError ? "bg-rose-50 border-rose-200 text-rose-700" :
            prewarm?.running ? "bg-amber-50 border-amber-200 text-amber-800" :
            "bg-emerald-50 border-emerald-200 text-emerald-700"
          }`}
          data-testid="atlas-blocks-prewarm-status"
        >
          {prewarm?.running && <Loader2 size={13} className="animate-spin" />}
          <div className="flex-1">
            {prewarmError ? (
              <>Erreur pré-traduction : {prewarmError}</>
            ) : prewarm?.running ? (
              <>Pré-traduction en cours · {prewarm.done_pairs || 0} / {prewarm.total_pairs || 0} langues · <b>{prewarm.current || "…"}</b></>
            ) : (
              <>Pré-traduction terminée ✓ {prewarm?.errors?.length ? `(${prewarm.errors.length} erreur${prewarm.errors.length > 1 ? "s" : ""})` : "Toutes les langues cachées, prêtes pour l'indexation SEO."}</>
            )}
          </div>
          {prewarm && !prewarm.running && (
            <button onClick={() => { setPrewarm(null); setPrewarmError(null); }} className="text-[11px] px-2 py-1 rounded hover:bg-white/50" data-testid="atlas-blocks-prewarm-dismiss">✕</button>
          )}
        </div>
      )}

      {job && (
        <div className={`border rounded-[5px] p-3 mb-4 text-[13px] flex items-center gap-2 ${
          job.status === "failed" ? "bg-rose-50 border-rose-200 text-rose-700" :
          job.status === "done" || job.status === "completed" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
          "bg-blue-50 border-blue-200 text-blue-700"
        }`} data-testid="atlas-blocks-job-progress">
          {generating && <Loader2 size={13} className="animate-spin" />}
          <div className="flex-1">
            <div className="font-medium">
              {job.status === "failed" ? "Génération échouée" :
               job.status === "done" || job.status === "completed" ? "Génération terminée ✓" :
               "Génération en cours…"}
            </div>
            <div className="text-[11.5px] opacity-80">
              {job.done_categories ?? 0} / {job.total_categories ?? "?"} catégories · {job.created_total ?? 0} blocs créés
              {job.errors?.length > 0 && ` · ${job.errors.length} erreur(s)`}
            </div>
          </div>
          {(job.status === "done" || job.status === "completed" || job.status === "failed") && (
            <button
              onClick={() => setJob(null)}
              className="text-[11px] px-2 py-1 rounded hover:bg-white/50"
              data-testid="atlas-blocks-job-dismiss"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-3 mb-4 text-[13px] text-rose-700 flex items-start gap-2" data-testid="atlas-blocks-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Category rail */}
        <nav className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-2 h-fit sticky top-4" data-testid="atlas-blocks-categories">
          {ATLAS_CATEGORIES.map((c) => {
            const count = (byCategory[c.key] || []).length;
            const active = activeCategory === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setActiveCategory(c.key)}
                className={`w-full text-left px-3 py-2 rounded-[4px] flex items-center justify-between gap-2 transition-colors ${
                  active ? "bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]" : "hover:bg-[color:var(--gv-mist)]/60 text-[color:var(--gv-ink)]"
                }`}
                data-testid={`atlas-blocks-cat-${c.key}`}
              >
                <span className="text-[13.5px] inline-flex items-center gap-2">
                  <span className="text-base">{c.emoji}</span> {c.label}
                </span>
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${count > 0 ? "bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/70" : "text-[color:var(--gv-ink)]/30"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Active category blocks */}
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4" data-testid="atlas-blocks-list">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg text-[color:var(--gv-ink)] inline-flex items-center gap-2">
              <span>{activeCat?.emoji}</span> {activeCat?.label} · <span className="text-[color:var(--gv-ink)]/50 text-sm font-normal">{activeBlocks.length} blocs</span>
            </h2>
            <button
              onClick={() => handleGenerateCategory(activeCategory)}
              disabled={generating}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[4px] border border-[color:var(--gv-pink)] text-[color:var(--gv-pink)] text-[12px] hover:bg-[color:var(--gv-pink)]/5 disabled:opacity-50"
              data-testid="atlas-blocks-generate-cat"
            >
              <Sparkles size={11} /> {activeBlocks.length === 0 ? "Générer cette catégorie" : "Étendre (+ blocs)"}
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center text-[color:var(--gv-ink)]/50 flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Chargement des blocs…
            </div>
          ) : activeBlocks.length === 0 ? (
            <EmptyCategory categoryLabel={activeCat?.label} onGenerate={() => handleGenerateCategory(activeCategory)} generating={generating} />
          ) : (
            <div className="space-y-2" data-testid="atlas-blocks-items">
              {activeBlocks.map((b, i) => (
                <BlockRow
                  key={b.id}
                  block={b}
                  idx={i}
                  total={activeBlocks.length}
                  onTogglePublic={handleTogglePublic}
                  onSave={handleSaveContent}
                  onDelete={handleDelete}
                  onMove={handleMove}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyCategory = ({ categoryLabel, onGenerate, generating }) => (
  <div className="p-8 text-center border-2 border-dashed border-[color:var(--gv-line)] rounded-[5px]">
    <div className="text-[color:var(--gv-ink)]/60 text-[13.5px] mb-3">
      Aucun bloc dans <b>{categoryLabel}</b> pour cette fiche.
    </div>
    <button
      onClick={onGenerate}
      disabled={generating}
      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[4px] bg-[color:var(--gv-pink)] text-white text-[13px] disabled:opacity-50"
      data-testid="atlas-blocks-empty-generate"
    >
      {generating ? <><Loader2 size={13} className="animate-spin" /> Génération…</> : <><Sparkles size={13} /> Générer 15+ blocs pour cette catégorie</>}
    </button>
  </div>
);

const BlockRow = ({ block, idx, total, onTogglePublic, onSave, onDelete, onMove }) => {
  const [content, setContent] = useState(block.content || block.body || "");
  const [title, setTitle] = useState(block.title || "");
  const dirty = content !== (block.content || block.body || "") || title !== (block.title || "");

  return (
    <div className={`border rounded-[5px] p-3 ${block.public ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30"}`} data-testid={`atlas-block-${block.id}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex flex-col gap-0.5 shrink-0">
          <button onClick={() => onMove(block, -1)} disabled={idx === 0} className="p-0.5 rounded hover:bg-white disabled:opacity-30" title="Monter" data-testid={`atlas-block-${block.id}-up`}>
            <ArrowUp size={12} />
          </button>
          <button onClick={() => onMove(block, 1)} disabled={idx === total - 1} className="p-0.5 rounded hover:bg-white disabled:opacity-30" title="Descendre" data-testid={`atlas-block-${block.id}-down`}>
            <ArrowDown size={12} />
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titre du bloc (optionnel)"
            className="w-full text-[14.5px] font-medium bg-transparent border-0 border-b border-transparent hover:border-[color:var(--gv-line)] focus:border-[color:var(--gv-pink)] focus:outline-none py-1"
            data-testid={`atlas-block-${block.id}-title`}
          />
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onTogglePublic(block)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-[4px] text-[11.5px] font-medium ${
              block.public ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"
            }`}
            title={block.public ? "Public (blog + explorer)" : "Enrichi (app seulement)"}
            data-testid={`atlas-block-${block.id}-public`}
          >
            {block.public ? <><Eye size={11} /> Public</> : <><EyeOff size={11} /> Enrichi</>}
          </button>
          {(dirty || block.__saving) && (
            <button
              onClick={() => onSave({ ...block, title }, content)}
              disabled={block.__saving}
              className="p-1.5 rounded hover:bg-white text-[color:var(--gv-pink)] disabled:opacity-50"
              title="Enregistrer"
              data-testid={`atlas-block-${block.id}-save`}
            >
              {block.__saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            </button>
          )}
          {block.__saved && <span className="text-[11px] text-emerald-600">✓</span>}
          <button
            onClick={() => onDelete(block)}
            className="p-1.5 rounded hover:bg-red-50 text-[color:var(--gv-ink)]/40 hover:text-red-600"
            title="Supprimer"
            data-testid={`atlas-block-${block.id}-delete`}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={Math.min(10, Math.max(3, Math.ceil((content?.length || 0) / 100)))}
        placeholder="Contenu du bloc…"
        className="w-full text-[13px] leading-relaxed bg-white/70 border border-[color:var(--gv-line)] rounded-[3px] p-2 resize-y focus:outline-none focus:border-[color:var(--gv-pink)]"
        data-testid={`atlas-block-${block.id}-content`}
      />
    </div>
  );
};

export default AdminAtlasBlocksPage;
